import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import axios from 'axios';

import {
  useNavigate
} from 'react-router-dom';

import {
  Building2,
  FileText,
  FlaskConical,
  LogOut,
  MapPin,
  Navigation,
  Pencil,
  Save,
  Search,
  ShieldAlert,
  Undo2,
  Users,
  RotateCcw,
  Eye
} from 'lucide-react';

import {
  CircleMarker,
  LayersControl,
  MapContainer,
  Polygon,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents
} from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import ModonLogo from '../components/ModonLogo';
import './StationDashboard.modon.css';


const DEFAULT_CENTER = [
  21.5433,
  39.1728
];


const RISK_COLOR = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
};


const unitLabel = (u) => ({
  liter: 'لتر',
  kg: 'كجم',
  ton: 'طن',
  count: 'عبوة',
}[u] || u || '');


const riskBadgeClass = (level) => {

  if (level === 'high') {
    return 'bg-red-100 text-red-700 border-red-200';
  }

  if (level === 'medium') {
    return 'bg-amber-100 text-amber-700 border-amber-200';
  }

  return 'bg-emerald-100 text-emerald-700 border-emerald-200';
};


function MapClickHandler({
  drawing,
  onAddPoint
}) {

  useMapEvents({
    click(e) {

      if (!drawing) return;

      onAddPoint([
        Number(
          e.latlng.lat.toFixed(7)
        ),
        Number(
          e.latlng.lng.toFixed(7)
        )
      ]);
    }
  });

  return null;
}


function FitBoundary({
  points
}) {

  const map = useMap();

  useEffect(() => {

    if (
      Array.isArray(points) &&
      points.length >= 2
    ) {
      map.fitBounds(
        points,
        {
          padding: [40, 40],
          maxZoom: 15,
        }
      );
    }

  }, [map, points]);

  return null;
}


function FlyToFactory({
  factory
}) {

  const map = useMap();

  useEffect(() => {

    if (
      factory &&
      Number.isFinite(
        Number(factory.lat)
      ) &&
      Number.isFinite(
        Number(factory.lng)
      )
    ) {

      map.flyTo(
        [
          Number(factory.lat),
          Number(factory.lng)
        ],
        17,
        {
          duration: 1
        }
      );
    }

  }, [
    map,
    factory
  ]);

  return null;
}


function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  className = ''
}) {

  return (
    <Card className="p-4">

      <div className="flex items-start justify-between">

        <div>

          <div className="text-xs text-slate-500">
            {label}
          </div>

          <div className={`text-2xl font-extrabold mt-1 ${className}`}>
            {value}
          </div>

          {sub && (
            <div className="text-[10px] text-slate-400 mt-1">
              {sub}
            </div>
          )}

        </div>

        <Icon
          size={24}
          className={
            className ||
            'text-[#1a4d80]'
          }
        />

      </div>

    </Card>
  );
}


export default function StationDashboard() {

  const nav = useNavigate();

  const [dashboard, setDashboard] =
    useState(null);

  const [error, setError] =
    useState('');

  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState('');

  const [selectedFactory, setSelectedFactory] =
    useState(null);

  const [drawing, setDrawing] =
    useState(false);

  const [boundaryPoints, setBoundaryPoints] =
    useState([]);

  const [savingBoundary, setSavingBoundary] =
    useState(false);


  const normalizeBoundary = (
    boundary
  ) => {

    if (!Array.isArray(boundary)) {
      return [];
    }

    const points =
      boundary
        .filter(
          p =>
            Array.isArray(p) &&
            p.length === 2
        )
        .map(
          ([lng, lat]) => [
            Number(lat),
            Number(lng)
          ]
        );

    if (points.length >= 2) {

      const first =
        points[0];

      const last =
        points[
          points.length - 1
        ];

      if (
        first[0] === last[0] &&
        first[1] === last[1]
      ) {
        points.pop();
      }
    }

    return points;
  };


  const loadDashboard = async () => {

    try {

      setLoading(true);
      setError('');

      const r =
        await axios.get(
          '/api/station/dashboard',
          {
            withCredentials: true
          }
        );

      setDashboard(r.data);

      setBoundaryPoints(
        normalizeBoundary(
          r.data?.station?.boundary
        )
      );

    } catch (e) {

      setError(
        e?.response?.data?.detail ||
        'تعذر تحميل لوحة المحطة'
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {
    loadDashboard();
  }, []);


  const factories =
    dashboard?.factories || [];

  const topRisk =
    dashboard?.topRiskFactories || [];

  const stats =
    dashboard?.stats || {};

  const station =
    dashboard?.station || {};


  const filteredFactories =
    useMemo(() => {

      const q =
        search
          .trim()
          .toLowerCase();

      if (!q) {
        return factories;
      }

      return factories.filter(
        (factory) => {

          const factoryText = [
            factory.factoryName,
            factory.activity,
            factory.city,
            factory.address,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          const chemicalText =
            (factory.chemicals || [])
              .map(
                c => [
                  c.name,
                  c.chemicalName,
                  c.unNumber,
                ]
                  .filter(Boolean)
                  .join(' ')
              )
              .join(' ')
              .toLowerCase();

          return (
            factoryText.includes(q) ||
            chemicalText.includes(q)
          );
        }
      );

    }, [
      factories,
      search
    ]);


  const mapFactories =
    factories.filter(
      f =>
        Number.isFinite(
          Number(f.lat)
        ) &&
        Number.isFinite(
          Number(f.lng)
        )
    );


  const addBoundaryPoint = (
    point
  ) => {

    setBoundaryPoints(
      prev => [
        ...prev,
        point
      ]
    );
  };


  const startBoundaryEdit = () => {
    setDrawing(true);
  };


  const clearBoundary = () => {
    setBoundaryPoints([]);
    setDrawing(true);
  };


  const undoBoundary = () => {

    setBoundaryPoints(
      prev =>
        prev.slice(
          0,
          -1
        )
    );
  };


  const cancelBoundary = () => {

    setBoundaryPoints(
      normalizeBoundary(
        station?.boundary
      )
    );

    setDrawing(false);
  };


  const saveBoundary = async () => {

    if (
      boundaryPoints.length < 3
    ) {
      setError(
        'يجب تحديد 3 نقاط على الأقل.'
      );
      return;
    }

    try {

      setSavingBoundary(true);
      setError('');

      await axios.put(
        '/api/station/boundary',
        {
          boundary:
            boundaryPoints.map(
              ([lat, lng]) => [
                lng,
                lat
              ]
            )
        },
        {
          withCredentials: true
        }
      );

      setDrawing(false);

      await loadDashboard();

    } catch (e) {

      setError(
        e?.response?.data?.detail ||
        'تعذر حفظ حدود المحطة'
      );

    } finally {

      setSavingBoundary(false);
    }
  };


  const showFactoryOnMap = (
    factory
  ) => {

    setSelectedFactory(factory);

    setTimeout(() => {

      document
        .getElementById(
          'station-map'
        )
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

    }, 50);
  };


  const openNavigation = (
    factory
  ) => {

    if (
      factory.lat == null ||
      factory.lng == null
    ) {
      return;
    }

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${factory.lat},${factory.lng}`,
      '_blank',
      'noopener,noreferrer'
    );
  };


  const logout = async () => {

    try {

      await axios.post(
        '/api/auth/logout',
        {},
        {
          withCredentials: true
        }
      );

    } catch {}

    nav('/login');
  };


  if (
    loading &&
    !dashboard
  ) {
    return (
      <div
        dir="rtl"
        className="station-modon-loading"
      >
        جارٍ تحميل لوحة المحطة...
      </div>
    );
  }


  return (
    <div
      dir="rtl"
      className="station-modon-shell"
    >

      <header className="station-modon-header">

        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">

          <div className="flex items-center gap-3">

            <ModonLogo size={44}/>

            <div>

              <h1 className="font-extrabold text-slate-900">
                {
                  station.name ||
                  'لوحة مدير المحطة'
                }
              </h1>

              <p className="text-xs text-slate-500">
                لوحة التشغيل والمخاطر الكيميائية
              </p>

            </div>

          </div>


          <div className="flex items-center gap-2">

            <Button
              variant="outline"
              data-testid="fire-access-btn"
              onClick={() =>
                nav('/station/fire-access')
              }
            >
              بوابة فريق الإطفاء / QR
            </Button>

            <Button
              variant="outline"
              onClick={logout}
            >
              <LogOut size={14}/>
              تسجيل الخروج
            </Button>

          </div>

        </div>

      </header>


      <main className="station-modon-main max-w-7xl mx-auto p-4 md:p-6 space-y-5">

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
            {typeof error === 'string'
              ? error
              : JSON.stringify(error)}
          </div>
        )}


        {/* KPI */}
        <div className="station-kpi-grid grid grid-cols-2 md:grid-cols-5 gap-3">

          <StatCard
            icon={Building2}
            label="المصانع"
            value={
              stats.factories ?? 0
            }
            className="text-[#1a4d80]"
          />

          <StatCard
            icon={FileText}
            label="تقارير SDS"
            value={
              stats.sds ?? 0
            }
            className="text-indigo-600"
          />

          <StatCard
            icon={FlaskConical}
            label="المواد الكيميائية"
            value={
              stats.uniqueChemicals ?? 0
            }
            sub={`${stats.chemicalRecords ?? 0} سجل مواد`}
            className="text-violet-600"
          />

          <StatCard
            icon={ShieldAlert}
            label="مصانع عالية الخطورة"
            value={
              stats.highRiskFactories ?? 0
            }
            className="text-red-600"
          />

          <StatCard
            icon={MapPin}
            label="حدود GPS"
            value={
              boundaryPoints.length >= 3
                ? 'محددة'
                : 'غير محددة'
            }
            className={
              boundaryPoints.length >= 3
                ? 'text-emerald-600'
                : 'text-amber-600'
            }
          />

        </div>


        {/* Risk summary */}
        <div className="station-risk-grid grid grid-cols-3 gap-3">

          <Card className="p-3 text-center border-red-200 bg-red-50">

            <div className="text-xs text-red-600">
              عالي
            </div>

            <div className="text-xl font-extrabold text-red-700">
              {
                stats.highRiskFactories ?? 0
              }
            </div>

          </Card>

          <Card className="p-3 text-center border-amber-200 bg-amber-50">

            <div className="text-xs text-amber-600">
              متوسط
            </div>

            <div className="text-xl font-extrabold text-amber-700">
              {
                stats.mediumRiskFactories ?? 0
              }
            </div>

          </Card>

          <Card className="p-3 text-center border-emerald-200 bg-emerald-50">

            <div className="text-xs text-emerald-600">
              منخفض
            </div>

            <div className="text-xl font-extrabold text-emerald-700">
              {
                stats.lowRiskFactories ?? 0
              }
            </div>

          </Card>

        </div>


        {/* Top 10 */}
        <Card className="station-panel overflow-hidden">

          <div className="station-panel-header p-4 border-b">

            <div className="flex items-center gap-2">

              <ShieldAlert
                size={19}
                className="text-red-600"
              />

              <h2 className="font-extrabold text-[#1a4d80]">
                أخطر 10 مصانع في نطاق المحطة
              </h2>

            </div>

            <p className="text-xs text-slate-500 mt-1">
              مرتبة حسب أعلى درجة مخاطر في تقارير SDS الحالية.
            </p>

          </div>


          {topRisk.length === 0 ? (

            <div className="p-8 text-center text-slate-500">
              لا توجد بيانات SDS كافية حتى الآن.
            </div>

          ) : (

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                <thead className="bg-slate-50 text-slate-600">

                  <tr>

                    <th className="p-3 text-right">
                      #
                    </th>

                    <th className="p-3 text-right">
                      المصنع
                    </th>

                    <th className="p-3 text-center">
                      الخطورة
                    </th>

                    <th className="p-3 text-center">
                      الدرجة
                    </th>

                    <th className="p-3 text-center">
                      المواد
                    </th>

                    <th className="p-3 text-center">
                      SDS
                    </th>

                    <th className="p-3">
                      الإجراءات
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {topRisk.map(
                    (
                      factory,
                      index
                    ) => (

                      <tr
                        key={factory.id}
                        className="border-t hover:bg-slate-50"
                      >

                        <td className="p-3 font-bold">
                          {index + 1}
                        </td>


                        <td className="p-3">

                          <div className="font-bold">
                            {factory.factoryName}
                          </div>

                          <div className="text-xs text-slate-500">
                            {factory.activity || '—'}
                          </div>

                        </td>


                        <td className="p-3 text-center">

                          <Badge
                            variant="outline"
                            className={
                              riskBadgeClass(
                                factory.riskLevel
                              )
                            }
                          >
                            {factory.riskLabel}
                          </Badge>

                        </td>


                        <td className="p-3 text-center font-extrabold">
                          {factory.riskScore}
                        </td>


                        <td className="p-3 text-center">
                          {factory.chemicalCount}
                        </td>


                        <td className="p-3 text-center">
                          {factory.sdsCount}
                        </td>


                        <td className="p-3">

                          <div className="flex gap-1 justify-center">

                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                showFactoryOnMap(
                                  factory
                                )
                              }
                            >
                              <MapPin size={13}/>
                              الخريطة
                            </Button>


                            {factory.primarySdsId && (
                              <Button
                                size="sm"
                                className="bg-[#1a4d80]"
                                onClick={() =>
                                  nav(
                                    `/station/sds/${factory.primarySdsId}`
                                  )
                                }
                              >
                                <FileText size={13}/>
                                SDS
                              </Button>
                            )}

                          </div>

                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

          )}

        </Card>


        {/* MAP */}
        <Card
          id="station-map"
          className="station-panel station-map-panel overflow-hidden"
        >

          <div className="p-4 border-b bg-white">

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">

              <div>

                <h2 className="font-extrabold text-[#1a4d80]">
                  خريطة مصانع المحطة
                </h2>

                <p className="text-xs text-slate-500 mt-1">
                  حدود المحطة وجميع المصانع التابعة لها حسب مستوى الخطورة.
                </p>

              </div>


              <div className="flex flex-wrap gap-2">

                {!drawing && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={
                      startBoundaryEdit
                    }
                  >
                    <Pencil size={14}/>
                    تعديل حدود المحطة
                  </Button>
                )}


                {drawing && (
                  <>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={
                        undoBoundary
                      }
                    >
                      <Undo2 size={14}/>
                      تراجع
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={
                        clearBoundary
                      }
                    >
                      <RotateCcw size={14}/>
                      إعادة الرسم
                    </Button>

                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={
                        savingBoundary ||
                        boundaryPoints.length < 3
                      }
                      onClick={
                        saveBoundary
                      }
                    >
                      <Save size={14}/>
                      حفظ الحدود
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={
                        cancelBoundary
                      }
                    >
                      إلغاء
                    </Button>

                  </>
                )}

              </div>

            </div>

          </div>


          <div
            style={{
              height: '560px'
            }}
          >

            <MapContainer
              center={DEFAULT_CENTER}
              zoom={12}
              className="h-full w-full"
            >

              <LayersControl
                position="topright"
              >

                <LayersControl.BaseLayer
                  checked
                  name="الخريطة"
                >

                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                </LayersControl.BaseLayer>


                <LayersControl.BaseLayer
                  name="قمر صناعي"
                >

                  <TileLayer
                    attribution="Tiles &copy; Esri"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />

                </LayersControl.BaseLayer>

              </LayersControl>


              <MapClickHandler
                drawing={drawing}
                onAddPoint={
                  addBoundaryPoint
                }
              />


              {boundaryPoints.length >= 2 && (
                <FitBoundary
                  points={
                    boundaryPoints
                  }
                />
              )}


              {selectedFactory && (
                <FlyToFactory
                  factory={
                    selectedFactory
                  }
                />
              )}


              {boundaryPoints.length >= 3 && (
                <Polygon
                  positions={
                    boundaryPoints
                  }
                  pathOptions={{
                    color: '#1a4d80',
                    weight: 4,
                    fillColor: '#2563eb',
                    fillOpacity: 0.10
                  }}
                />
              )}


              {boundaryPoints.map(
                (
                  point,
                  index
                ) => (
                  <CircleMarker
                    key={
                      `boundary-${index}`
                    }
                    center={point}
                    radius={6}
                    pathOptions={{
                      color: '#fff',
                      weight: 2,
                      fillColor: '#1a4d80',
                      fillOpacity: 1
                    }}
                  >
                    <Tooltip>
                      نقطة حدود {index + 1}
                    </Tooltip>
                  </CircleMarker>
                )
              )}


              {mapFactories.map(
                factory => (

                  <CircleMarker
                    key={factory.id}
                    center={[
                      Number(factory.lat),
                      Number(factory.lng)
                    ]}
                    radius={
                      selectedFactory?.id ===
                      factory.id
                        ? 11
                        : 8
                    }
                    pathOptions={{
                      color: '#fff',
                      weight: 2,
                      fillColor:
                        RISK_COLOR[
                          factory.riskLevel
                        ] ||
                        RISK_COLOR.low,
                      fillOpacity: 0.95
                    }}
                  >

                    <Tooltip>
                      <div dir="rtl">
                        <b>
                          {factory.factoryName}
                        </b>
                        <div>
                          الخطورة:
                          {' '}
                          {factory.riskLabel}
                        </div>
                      </div>
                    </Tooltip>


                    <Popup>

                      <div
                        dir="rtl"
                        style={{
                          minWidth: 220
                        }}
                      >

                        <div className="font-extrabold text-base">
                          {factory.factoryName}
                        </div>

                        <div className="text-xs text-slate-500 mt-1">
                          {factory.activity || '—'}
                        </div>

                        <div className="mt-2 text-xs">
                          الخطورة:
                          {' '}
                          <b>
                            {factory.riskLabel}
                          </b>
                          {' '}
                          ({factory.riskScore})
                        </div>

                        <div className="text-xs mt-1">
                          المواد:
                          {' '}
                          <b>
                            {factory.chemicalCount}
                          </b>
                        </div>

                        <div className="mt-3 flex gap-1">

                          {factory.primarySdsId && (
                            <button
                              onClick={() =>
                                nav(
                                  `/station/sds/${factory.primarySdsId}`
                                )
                              }
                              className="rounded bg-[#1a4d80] text-white px-2 py-1 text-xs"
                            >
                              فتح SDS
                            </button>
                          )}

                          <button
                            onClick={() =>
                              openNavigation(
                                factory
                              )
                            }
                            className="rounded border px-2 py-1 text-xs"
                          >
                            <Navigation
                              size={11}
                              className="inline ml-1"
                            />
                            الانتقال
                          </button>

                        </div>

                      </div>

                    </Popup>

                  </CircleMarker>

                )
              )}

            </MapContainer>

          </div>

        </Card>


        {/* SEARCH */}
        <Card className="p-4">

          <div className="relative">

            <Search
              size={17}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <Input
              value={search}
              onChange={
                e =>
                  setSearch(
                    e.target.value
                  )
              }
              placeholder="بحث باسم المصنع، النشاط، المادة الكيميائية أو رقم UN..."
              className="pr-10"
            />

          </div>

        </Card>


        {/* FACTORIES */}
        <div className="space-y-3">

          <div className="flex items-center gap-2">

            <Users
              className="text-[#1a4d80]"
            />

            <h2 className="font-extrabold text-[#1a4d80] text-lg">
              مصانع المحطة
            </h2>

            <Badge variant="secondary">
              {filteredFactories.length}
            </Badge>

          </div>


          {filteredFactories.length === 0 ? (

            <Card className="p-8 text-center text-slate-500">
              لا توجد مصانع مطابقة.
            </Card>

          ) : (

            <div className="grid xl:grid-cols-2 gap-4">

              {filteredFactories.map(
                factory => (

                  <Card
                    key={factory.id}
                    className="p-5"
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div>

                        <h3 className="font-extrabold text-lg">
                          {factory.factoryName}
                        </h3>

                        <div className="text-xs text-slate-500 mt-1">
                          {factory.activity || '—'}
                          {' • '}
                          {factory.city || '—'}
                        </div>

                      </div>


                      <Badge
                        variant="outline"
                        className={
                          riskBadgeClass(
                            factory.riskLevel
                          )
                        }
                      >
                        {factory.riskLabel}
                        {' '}
                        {factory.riskScore}
                      </Badge>

                    </div>


                    <div className="grid grid-cols-3 gap-2 mt-4 text-center">

                      <div className="rounded-lg bg-slate-50 p-2">

                        <div className="text-[10px] text-slate-500">
                          المواد
                        </div>

                        <div className="font-bold">
                          {factory.chemicalCount}
                        </div>

                      </div>

                      <div className="rounded-lg bg-slate-50 p-2">

                        <div className="text-[10px] text-slate-500">
                          SDS
                        </div>

                        <div className="font-bold">
                          {factory.sdsCount}
                        </div>

                      </div>

                      <button
                        onClick={() =>
                          showFactoryOnMap(
                            factory
                          )
                        }
                        className="rounded-lg bg-blue-50 p-2 hover:bg-blue-100"
                      >

                        <div className="text-[10px] text-blue-600">
                          الخريطة
                        </div>

                        <MapPin
                          size={18}
                          className="mx-auto mt-1 text-[#1a4d80]"
                        />

                      </button>

                    </div>


                    <div className="mt-4">

                      <div className="font-bold text-xs text-slate-600 mb-2">
                        المواد والكميات
                      </div>


                      {factory.chemicals.length === 0 ? (

                        <div className="text-xs text-slate-400">
                          لا توجد مواد مسجلة.
                        </div>

                      ) : (

                        <div className="space-y-1.5 max-h-52 overflow-y-auto">

                          {factory.chemicals.map(
                            (
                              chem,
                              index
                            ) => (

                              <div
                                key={`${chem.reportId}-${index}`}
                                className="rounded-lg border border-slate-200 p-2 flex items-center justify-between gap-2"
                              >

                                <div>

                                  <div className="text-xs font-bold">
                                    {chem.name}
                                  </div>

                                  <div className="text-[10px] text-slate-500">
                                    {chem.unNumber
                                      ? `UN ${chem.unNumber}`
                                      : chem.reportTitle}
                                  </div>

                                </div>


                                <div className="text-xs font-bold whitespace-nowrap">

                                  {
                                    chem.quantity !== '' &&
                                    chem.quantity != null
                                      ? `${chem.quantity} ${unitLabel(chem.unit)}`
                                      : '—'
                                  }

                                </div>

                              </div>

                            )
                          )}

                        </div>

                      )}

                    </div>


                    {factory.reports.length > 0 && (

                      <div className="mt-4 border-t pt-3">

                        <div className="font-bold text-xs text-slate-600 mb-2">
                          ملفات SDS
                        </div>

                        <div className="flex flex-wrap gap-2">

                          {factory.reports.map(
                            report => (

                              <Button
                                key={report.id}
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  nav(
                                    `/station/sds/${report.id}`
                                  )
                                }
                              >
                                <Eye size={13}/>
                                {
                                  report.title ||
                                  'فتح SDS'
                                }
                              </Button>

                            )
                          )}

                        </div>

                      </div>

                    )}

                  </Card>

                )
              )}

            </div>

          )}

        </div>

      </main>

    </div>
  );
}
